import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import Switch from '@mui/material/Switch';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { traerEventosTodosPersonal } from '../../../../store/agenda/thunkAgenda';
// Importar Tippy correctamente
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light.css';

const AgendaPersonal = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [eventosAPI, setEventosAPI] = useState([])
    const calendarRef = useRef(null)
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
    const [currentView, setCurrentView] = useState(window.innerWidth < 768 ? 'listWeek' : 'dayGridMonth')
    // Añadir estado para controlar la carga
    const [cargando, setCargando] = useState(false)

    // Estados para los filtros activos - inicializados con objetos vacíos
    const [filtros, setFiltros] = useState({})
    const [filtrosVendedores, setFiltrosVendedores] = useState({})

    // Estados para rastrear si los filtros ya fueron inicializados
    const [filtrosInicializados, setFiltrosInicializados] = useState(false)
    const [filtrosVendedoresInicializados, setFiltrosVendedoresInicializados] = useState(false)

    // Estados para fechas de filtro
    const [fechaInicioFiltro, setFechaInicioFiltro] = useState('')
    const [fechaFinFiltro, setFechaFinFiltro] = useState('')

    // Función para ir a la página de creación de evento
    const handleCrearEvento = () => {
        navigate('/agenda/crear');
    }

    // Función para manejar el clic en una fecha del calendario
    const handleDateClick = (info) => {
        // La fecha viene en formato YYYY-MM-DD
        const fechaSeleccionada = info.dateStr;
        navigate(`/agenda/crear?data=${fechaSeleccionada}`);
    }

    // Añadir esta nueva función para manejar clics en eventos
    const handleEventClick = (info) => {
        // Obtener el ID del evento desde el objeto de evento
        const eventId = info.event.id;
        // Navegar a la página de edición con el ID como parámetro
        navigate(`/agenda/editar?data=${eventId}`);
    }

    // Detectar cambios de tamaño de la ventana para ajustar la vista
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile && calendarRef.current) {
                // En móvil siempre usar vista lista
                setCurrentView('listWeek');
                calendarRef.current.getApi().changeView('listWeek');
            } else if (!mobile && currentView === 'listWeek' && calendarRef.current) {
                // Si vuelve a desktop desde móvil, cambiar a vista mensual por defecto
                setCurrentView('dayGridMonth');
                calendarRef.current.getApi().changeView('dayGridMonth');
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [currentView])

    // Función para cambiar de vista manualmente (solo en PC)
    const handleViewChange = (viewName) => {
        setCurrentView(viewName)
        if (calendarRef.current) {
            calendarRef.current.getApi().changeView(viewName)
        }
    }

    /**
     * @description Aplica los filtros de fecha seleccionados
     */
    const aplicarFiltrosFecha = async () => {
        try {
            // Validar que ambas fechas estén presentes
            if (!fechaInicioFiltro || !fechaFinFiltro) {
                Swal.fire({
                    title: 'Error',
                    text: 'Debes seleccionar ambas fechas',
                    icon: 'warning',
                    confirmButtonText: 'Aceptar'
                });
                return;
            }

            // Validar que la fecha de inicio sea menor que la fecha de fin
            if (new Date(fechaInicioFiltro) > new Date(fechaFinFiltro)) {
                Swal.fire({
                    title: 'Error',
                    text: 'La fecha de inicio debe ser anterior a la fecha de fin',
                    icon: 'warning',
                    confirmButtonText: 'Aceptar'
                });
                return;
            }

            // Mostrar indicador de carga
            setCargando(true);
            Swal.fire({
                title: 'Filtrando eventos',
                html: 'Por favor espere mientras actualizamos su agenda personal...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => Swal.showLoading()
            });

            // Obtener datos filtrados por fecha
            const result = await dispatch(traerEventosTodosPersonal('pendiente', fechaInicioFiltro, fechaFinFiltro));
            setEventosAPI(result.data.data || []);

            // Cerrar indicador de carga
            Swal.close();
            setCargando(false);

            // Notificar éxito
            Swal.fire({
                title: 'Filtrado completado',
                text: 'Se han actualizado los eventos según el rango de fechas seleccionado',
                icon: 'success',
                confirmButtonText: 'Aceptar'
            });
        } catch (error) {
            console.error('Error al filtrar la agenda personal:', error);
            Swal.close();
            setCargando(false);

            Swal.fire({
                title: 'Error',
                text: 'No se pudieron filtrar los eventos de la agenda personal',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    // Cargar datos desde API
    useEffect(() => {
        const fetchAgenda = async () => {
            try {
                // Mostrar SweetAlert de carga
                setCargando(true);
                Swal.fire({
                    title: 'Cargando eventos',
                    html: 'Por favor espere mientras cargamos su agenda personal...',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                // Obtener la fecha actual
                const hoy = new Date();

                // Establecer fecha de inicio una semana antes
                const fechaInicioTemp = new Date(hoy);
                fechaInicioTemp.setDate(fechaInicioTemp.getDate() - 7); // Resta 7 días
                const fechaInicio = fechaInicioTemp.toISOString().split('T')[0];

                // Crear fecha para 12 meses después
                const fechaFin = new Date(hoy);
                fechaFin.setMonth(hoy.getMonth() + 12);

                // Formatear la fecha fin en YYYY-MM-DD
                const fechaFinFormateada = fechaFin.toISOString().split('T')[0];

                // Establecer fechas en los filtros
                setFechaInicioFiltro(fechaInicio);
                setFechaFinFiltro(fechaFinFormateada);

                // Llamar a la función con los parámetros requeridos
                const result = await dispatch(traerEventosTodosPersonal('pendiente', fechaInicio, fechaFinFormateada));

                // Guardar los datos en el estado
                setEventosAPI(result.data.data || []);

                // Ocultar SweetAlert de carga
                Swal.close();

            } catch (error) {
                console.error('Error al cargar la agenda:', error);
                // Ocultar SweetAlert de carga en caso de error
                Swal.close();
            } finally {
                // Asegurarse de que el estado de carga se desactive
                setCargando(false);
            }
        };

        fetchAgenda();
    }, [dispatch])

    // Transformar datos de la API al formato necesario para FullCalendar
    const eventos = eventosAPI.map(evento => ({
        id: evento.id_agenda,
        title: evento.nombre_agenda,
        date: evento.fecha_inicio_agenda.split('T')[0],
        start: `${evento.fecha_inicio_agenda.split('T')[0]}T${evento.hora_inicio_agenda}`,
        end: `${evento.fecha_fin_agenda.split('T')[0]}T${evento.hora_fin_agenda}`,
        type: evento.tipo_agenda,
        backgroundColor: evento.color_agenda, // Propiedad correcta para FullCalendar
        borderColor: evento.color_agenda,     // Añadido para consistencia visual
        description: evento.descripcion_agenda,
        name_admin: evento.name_admin,
        color_agenda: evento.color_agenda,     // Mantener la referencia original
        nombre_cliente: evento.nombre_cliente,
        nombre_empresa: evento.nombre_empresa,
        estado_agenda: evento.estado_agenda
    }))

    // Obtener tipos y vendedores únicos
    const tipos = [...new Set(eventos.map(e => e.type).filter(Boolean))]
    const vendedores = [...new Set(eventos.map(e => e.name_admin).filter(Boolean))]

    // Inicializar filtros SOLO una vez cuando hay datos disponibles
    useEffect(() => {
        if (tipos.length > 0 && !filtrosInicializados) {
            const nuevosFiltros = tipos.reduce((acc, tipo) => ({ ...acc, [tipo]: true }), {});
            setFiltros(nuevosFiltros);
            setFiltrosInicializados(true);
        }
    }, [tipos, filtrosInicializados]);

    // Inicializar filtros de vendedores SOLO una vez cuando hay datos disponibles
    useEffect(() => {
        if (vendedores.length > 0 && !filtrosVendedoresInicializados) {
            const nuevosFiltrosVendedores = vendedores.reduce((acc, vendedor) => ({ ...acc, [vendedor]: true }), {});
            setFiltrosVendedores(nuevosFiltrosVendedores);
            setFiltrosVendedoresInicializados(true);
        }
    }, [vendedores, filtrosVendedoresInicializados]);

    // Manejar cambio en los checkboxes de tipos
    const handleFiltroChange = (tipo) => {
        setFiltros(prevFiltros => ({
            ...prevFiltros,
            [tipo]: !prevFiltros[tipo]
        }));
    }

    // Manejar cambio en los checkboxes de vendedores
    const handleFiltroVendedorChange = (vendedor) => {
        setFiltrosVendedores(prevFiltros => ({
            ...prevFiltros,
            [vendedor]: !prevFiltros[vendedor]
        }));
    }

    // Filtrar eventos según ambos filtros activos
    const eventosFiltrados = eventos.filter(e =>
        (e.type && filtros[e.type] === true) &&
        (!e.name_admin || filtrosVendedores[e.name_admin] === true)
    );

    // Efecto para ajustar el zoom al 80% cuando se carga el componente
    useEffect(() => {
        // Guardar el nivel de zoom actual para restaurarlo después si es necesario
        const originalZoom = document.body.style.zoom || '100%';

        // Establecer zoom al 80%
        if ('zoom' in document.body.style) {
            document.body.style.zoom = '80%';
        } else {
            // Alternativa para navegadores que no soportan zoom directo
            document.documentElement.style.transform = 'scale(0.80)';
            document.documentElement.style.transformOrigin = '0 0';
        }

        // Restaurar el zoom original cuando el componente se desmonte
        return () => {
            if ('zoom' in document.body.style) {
                document.body.style.zoom = originalZoom;
            } else {
                document.documentElement.style.transform = 'none';
                document.documentElement.style.transformOrigin = 'initial';
            }
        };
    }, []);

    // Agregar estilos CSS para mejor visualización
    useEffect(() => {
        // Añadir estilos específicos para la vista semanal
        const styleElement = document.createElement('style');
        styleElement.textContent = `
            .fc-dayGridWeek-view .fc-daygrid-day {
                height: auto !important;
                min-height: 100px;
            }
            .fc-event-no-padding {
                padding: 0 !important;
                margin: 1px 0 !important;
            }
            /* Mejorar visibilidad en vista semanal */
            .fc-dayGridWeek-view .fc-daygrid-day-events {
                margin-bottom: 0;
                padding: 2px;
                position: relative;
            }
            .fc-dayGridWeek-view .fc-daygrid-day-top {
                justify-content: center;
                padding-top: 5px;
            }
        `;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '2rem' }}>
            {/* Filtros a la izquierda (o arriba en móvil) */}
            <div className={isMobile ? "col-12" : "col-md-6 col-xxl-3"}>
                <div className="card price-card mb-4">
                    <div className="card-body price-head bg-light-secondary">
                        <h5 className="text-secondary">Filtrar por Tipo</h5>
                        <div className="price-icon bg-light-secondary">
                            <i className="fas fa-filter"></i>
                        </div>
                    </div>
                    <div className="card-body">
                        <ul className="list-unstyled product-list">
                            {tipos.map(tipo => (
                                <li className="enable" key={tipo}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.1rem' }}>
                                        <Switch
                                            checked={filtros[tipo] === true}
                                            onChange={() => handleFiltroChange(tipo)}
                                            color="primary"
                                            sx={{ mr: 1 }}
                                        />
                                        {tipo}
                                    </label>
                                </li>
                            ))}
                        </ul>
                        <div className="d-grid">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={handleCrearEvento}
                            >
                                Crear un nuevo evento
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filtro por Fecha (NUEVO) */}
                <div className="card price-card mb-4">
                    <div className="card-body price-head bg-light-secondary">
                        <h5 className="text-secondary">Filtrar por Fecha</h5>
                        <div className="price-icon bg-light-secondary">
                            <i className="fas fa-calendar-alt"></i>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="mb-3">
                            <label htmlFor="fechaInicio" className="form-label">Fecha inicio:</label>
                            <input
                                type="date"
                                className="form-control"
                                id="fechaInicio"
                                value={fechaInicioFiltro}
                                onChange={(e) => setFechaInicioFiltro(e.target.value)}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="fechaFin" className="form-label">Fecha fin:</label>
                            <input
                                type="date"
                                className="form-control"
                                id="fechaFin"
                                value={fechaFinFiltro}
                                onChange={(e) => setFechaFinFiltro(e.target.value)}
                            />
                        </div>
                        <div className="d-grid">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={aplicarFiltrosFecha}
                                disabled={cargando}
                            >
                                {cargando ? 'Aplicando...' : 'Aplicar filtro'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Calendario a la derecha (o abajo en móvil) */}
            <div style={{ flex: 1 }}>
                {!isMobile && (
                    <div style={{ marginBottom: '1rem' }}>
                        <button
                            className={`btn ${currentView === 'dayGridMonth' ? 'btn-secondary' : 'btn-outline-secondary'} me-2`}
                            onClick={() => handleViewChange('dayGridMonth')}
                        >
                            Vista Mensual
                        </button>
                        <button
                            className={`btn ${currentView === 'dayGridWeek' ? 'btn-secondary' : 'btn-outline-secondary'} me-2`}
                            onClick={() => handleViewChange('dayGridWeek')}
                        >
                            Vista Semanal
                        </button>
                        <button
                            className={`btn ${currentView === 'listWeek' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            onClick={() => handleViewChange('listWeek')}
                        >
                            Vista Agenda
                        </button>
                    </div>
                )}
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                    initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
                    events={eventosFiltrados}
                    locale="es"
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    selectable={true}
                    eventTimeFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    }}
                    // Usar las propiedades nativas de FullCalendar para los colores
                    eventDidMount={(info) => {
                        // Aplicar color a todo el fondo del evento
                        const color = info.event.extendedProps.color_agenda || '#cccccc';
                        const textColor = getTextColorForBackground(color);

                        // Seleccionar el evento principal y aplicar estilos
                        const eventEl = info.el;

                        if (eventEl) {
                            // Aplicar estilos al elemento del evento completo
                            eventEl.style.backgroundColor = color;
                            eventEl.style.borderColor = color;
                            eventEl.style.color = textColor;

                            // Centrar el contenido del evento
                            const mainContentEl = eventEl.querySelector('.fc-event-main');
                            if (mainContentEl) {
                                mainContentEl.style.textAlign = 'center';
                            }

                            // Para vista de lista, necesitamos ajustar el elemento dot
                            const dotEl = eventEl.querySelector('.fc-list-event-dot');
                            if (dotEl) {
                                dotEl.style.borderColor = color;
                            }

                            // Centrar también en la vista de lista
                            const titleEl = eventEl.querySelector('.fc-list-event-title');
                            if (titleEl) {
                                titleEl.style.textAlign = 'center';
                            }

                            // Crear contenido del tooltip con formato HTML
                            const tooltipContent = `
                                <div style="text-align: left; font-size: 12px;">
                                    <strong>Título:</strong> ${info.event.title}<br>
                                    <strong>Tipo:</strong> ${info.event.extendedProps.type || ''}<br>
                                    <strong>Responsable:</strong> ${info.event.extendedProps.name_admin || ''}<br>
                                    <strong>Cliente:</strong> ${info.event.extendedProps.nombre_cliente || ''}<br>
                                    <strong>Empresa:</strong> ${info.event.extendedProps.nombre_empresa || ''}<br>
                                    <strong>Estado:</strong> ${info.event.extendedProps.estado_agenda || ''}<br>
                                    <strong>Descripción:</strong> ${info.event.extendedProps.description || ''}
                                </div>
                            `;

                            // Inicializar Tippy.js
                            tippy(eventEl, {
                                content: tooltipContent,
                                allowHTML: true,
                                theme: 'light',
                                placement: 'top',
                                interactive: true,
                                maxWidth: 300
                            });
                        }
                    }}
                    eventContent={(eventInfo) => {
                        return (
                            <>
                                {eventInfo.timeText && (
                                    <div className="fc-event-time" style={{
                                        fontWeight: 'bold',
                                        wordBreak: 'break-word',
                                        textAlign: 'center',
                                        width: '100%'
                                    }}>
                                        {eventInfo.timeText}
                                    </div>
                                )}
                                <div className="fc-event-title-container" style={{
                                    textAlign: 'center',
                                    width: '100%'
                                }}>
                                    <div className="fc-event-title" style={{
                                        wordBreak: 'break-word',
                                        marginTop: '2px',
                                        marginBottom: '2px'
                                    }}>
                                        {eventInfo.event.title}
                                    </div>
                                    {eventInfo.event.extendedProps.name_admin && (
                                        <div className="fc-event-admin" style={{
                                            fontSize: '0.8em',
                                            opacity: 0.9,
                                            wordBreak: 'break-word'
                                        }}>
                                            {eventInfo.event.extendedProps.name_admin}
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    }}
                    eventClassNames="fc-event-no-padding"
                />
            </div>
        </div>
    );
};

// Función para determinar si el texto debe ser oscuro o claro según el color de fondo
const getTextColorForBackground = (backgroundColor) => {
    // Si no hay color de fondo, usar texto oscuro por defecto
    if (!backgroundColor) return '#000000';

    // Convertir el color hex a RGB
    let hex = backgroundColor.replace('#', '');
    if (hex.length === 3) {
        hex = hex.split('').map(h => h + h).join('');
    }

    // Extraer componentes RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calcular brillo percibido
    // Fórmula: (R * 0.299 + G * 0.587 + B * 0.114) > 186 usa texto oscuro, de lo contrario texto claro
    const brightness = (r * 0.299 + g * 0.587 + b * 0.114);

    return brightness > 150 ? '#000000' : '#ffffff';
};

export default AgendaPersonal;
